import { createRouter, createWebHistory } from 'vue-router'

import { useBookingStore } from '@/stores/booking'

/**
 * Five screens in one direction: dates, rooms, details, summary, confirmed.
 *
 * The paths are Slovenian because they are read by the guest — this is the one part of
 * the product a stranger sees in an address bar, and /sobe is a word they know while
 * /rooms is our word for it. The route NAMES stay English, as everything in the code
 * does.
 *
 * The guard is the interesting part. Each step needs the one before it: /povzetek with
 * no room chosen is a summary of nothing, and it is reachable by anybody who bookmarks
 * it or presses reload after the store has been emptied. Rather than draw a broken
 * page, the guard sends them back to the last step that still holds — which for
 * somebody arriving cold is the beginning.
 */
const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior: () => ({ top: 0 }),
    routes: [
        { path: '/',          name: 'search',  component: () => import('@/views/Search.vue') },
        { path: '/sobe',      name: 'rooms',   component: () => import('@/views/Rooms.vue') },
        { path: '/podatki',   name: 'guest',   component: () => import('@/views/Guest.vue') },
        { path: '/povzetek',  name: 'summary', component: () => import('@/views/Summary.vue') },
        { path: '/potrjeno',  name: 'done',    component: () => import('@/views/Done.vue') },
        { path: '/zaprto',    name: 'closed',  component: () => import('@/views/Closed.vue') },
        { path: '/:rest(.*)*', redirect: { name: 'search' } },
    ],
})

router.beforeEach((to) => {
    const store = useBookingStore()

    // The campaign is read from the address the visitor ARRIVED on, which is this
    // first navigation and no other. It is done in the guard rather than in a view so
    // that it cannot be missed by whichever screen a link happens to point at.
    store.readAttribution(to.query as Record<string, unknown>)

    // And the offer that sent them, on the same first navigation and for the same
    // reason it is done here rather than in a view: the landing page's Rezerviraj
    // button points at whichever screen it likes, and a token read by only one of them
    // is a guest quoted the price they came here to avoid (docs/MARKETING.md §10).
    store.readOfferLink(to.query as Record<string, unknown>)

    if (to.name === 'closed' || to.name === 'search') return true

    if (to.name === 'done') {
        // Either the booking was just made in this tab, or the guest is coming back
        // from Stripe's page onto a cold load — in which case the confirmation is put
        // back from where startPayment left it.
        return store.confirmation || store.restorePayment() ? true : { name: 'search' }
    }

    if (!store.offers) return { name: 'search' }

    if (to.name === 'rooms') return true

    return store.chosen ? true : { name: 'rooms' }
})

export default router
